/**
 * completePartialMarkdown — close markdown constructs that the stream has opened
 * but not yet finished, so an in-flight buffer renders in its final styled form
 * instead of as literal syntax.
 *
 * Without it, streaming markdown shows the reader `**Toyota Coro` and
 * `[Yaris - €16,950](htt` for as long as the rest of the token takes to land,
 * and the text then reflows when the closing delimiter arrives. With it, each
 * construct renders styled from its first character and never moves again.
 *
 * Only the block at the streaming edge is touched. Everything above the last
 * blank line is already settled, and markdown lets neither emphasis nor a link
 * span a blank line — so "completing" an earlier paragraph would render it
 * differently from the finished message, which is the very reflow this exists
 * to prevent.
 *
 * Two rules keep it from making things worse:
 *  - A delimiter that opened with nothing after it is STRIPPED, not closed.
 *    Closing a trailing `**` would emit `****`, which is its own literal flash.
 *  - An unterminated link renders as its label text only. Closing it with `)`
 *    would produce a live anchor pointing at a truncated URL.
 *
 * `_`/`__` are deliberately not treated as emphasis: GFM disables intra-word
 * underscore emphasis anyway, and underscores are common inside the model codes
 * and URLs this widget streams.
 *
 * Idempotent — text with nothing open is returned unchanged, so it is safe to
 * run on the settled final message too.
 */

/** Opening/closing fence of a code block, with markdown's ≤3-space indent. */
const FENCE_RE = /^ {0,3}(?:`{3,}|~{3,})/;

/** Longest first, so `**` is matched before `*`. */
const EMPHASIS = ['**', '~~', '*'];

/**
 * Blank out code so the emphasis scanner cannot see markdown characters that
 * are literal text. Lengths and newlines are preserved, so indices still line
 * up with the original string.
 *
 * @returns {{ masked: string, fenceAt: number }} `fenceAt` is the offset of an
 *   unterminated fence, or -1 when every fence is closed.
 */
function maskCode(text) {
  const lines = text.split('\n');
  const out = [];
  let fenceAt = -1;
  let offset = 0;

  for (const line of lines) {
    if (FENCE_RE.test(line)) {
      fenceAt = fenceAt === -1 ? offset : -1;
      out.push(' '.repeat(line.length));
    } else if (fenceAt !== -1) {
      out.push(' '.repeat(line.length));
    } else {
      out.push(maskTicks(line));
    }
    offset += line.length + 1;
  }

  return { masked: out.join('\n'), fenceAt };
}

/**
 * Blank out inline code spans within a single line. An unterminated backtick is
 * left visible to the scanner, which closes it at the end of the buffer.
 */
function maskTicks(line) {
  const chars = line.split('');
  let open = -1;

  for (let i = 0; i < chars.length; i += 1) {
    if (chars[i] !== '`') continue;
    if (open === -1) {
      open = i;
    } else {
      for (let j = open; j <= i; j += 1) chars[j] = ' ';
      open = -1;
    }
  }

  return chars.join('');
}

/**
 * Offset just past the last blank line before `end` — the start of the block
 * still being written.
 */
function lastBlockStart(masked, end) {
  const boundary = /\n[ \t]*\n/g;
  let start = 0;
  let match = boundary.exec(masked);

  while (match !== null && match.index + match[0].length <= end) {
    start = match.index + match[0].length;
    boundary.lastIndex = start;
    match = boundary.exec(masked);
  }

  return start;
}

/** Whitespace, or past the end of the buffer. */
function isSpace(ch) {
  return ch === undefined || /\s/.test(ch);
}

/**
 * Locate an unterminated `[label](url` or `[label`.
 *
 * @returns {{ start: number, label: string } | null}
 */
function findOpenLink(masked, text, end) {
  let bracket = -1;
  let labelEnd = -1;

  for (let i = 0; i < end; i += 1) {
    const ch = masked[i];
    if (ch === '\\') {
      i += 1;
    } else if (ch === '[' && labelEnd === -1) {
      bracket = i;
    } else if (ch === ']' && bracket !== -1) {
      // `](` continues into a destination; anything else ends the construct.
      if (masked[i + 1] === '(') {
        labelEnd = i;
        i += 1;
      } else {
        bracket = -1;
      }
    } else if (ch === ')' && labelEnd !== -1) {
      bracket = -1;
      labelEnd = -1;
    }
  }

  if (bracket === -1) return null;
  const stop = labelEnd === -1 ? end : labelEnd;
  return { start: bracket, label: text.slice(bracket + 1, stop) };
}

/**
 * Append the closers for every emphasis delimiter and code span still open,
 * dropping any that opened at the very end of the buffer with no content.
 */
function closeInline(text, masked, end) {
  // Closers go before any trailing whitespace: a `**` preceded by a space
  // cannot close, so `**Toyota ` + `**` would render both runs literally. The
  // same boundary decides what counts as "flush against the streaming edge".
  let contentEnd = end;
  while (contentEnd > 0 && isSpace(masked[contentEnd - 1])) contentEnd -= 1;

  const open = [];
  let i = 0;

  while (i < contentEnd) {
    const ch = masked[i];

    if (ch === '\\') {
      i += 2;
      continue;
    }

    if (ch === '`') {
      open.push({ mark: '`', at: i });
      i += 1;
      continue;
    }

    const mark = EMPHASIS.find((candidate) => masked.startsWith(candidate, i));
    if (mark) {
      // CommonMark flanking: a run followed by whitespace cannot open emphasis,
      // one preceded by whitespace cannot close it. Without this, the `*` in
      // `5 * 3` reads as an opener and the buffer grows a stray delimiter. The
      // run may legitimately sit flush against the streaming edge with its
      // content still in flight — that case is dropped further down.
      const canOpen = i + mark.length >= contentEnd || !isSpace(masked[i + mark.length]);
      const canClose = i > 0 && !isSpace(masked[i - 1]);
      const last = open[open.length - 1];

      if (last && last.mark === mark && canClose) open.pop();
      else if (canOpen) open.push({ mark, at: i });

      i += mark.length;
      continue;
    }

    i += 1;
  }

  if (open.length === 0) return text;

  // A delimiter opened flush against the end of the buffer has no content to
  // style — drop it rather than emit an empty `****`.
  const trailing = open[open.length - 1];
  if (trailing.at + trailing.mark.length >= contentEnd) {
    const trimmed = text.slice(0, trailing.at) + text.slice(contentEnd);
    return closeInline(trimmed, maskCode(trimmed).masked, trailing.at);
  }

  const closers = open
    .map((entry) => entry.mark)
    .reverse()
    .join('');

  return text.slice(0, contentEnd) + closers + text.slice(contentEnd);
}

/**
 * @param {string} text In-flight markdown buffer.
 * @returns {string} The same buffer with open constructs closed or dropped.
 */
export function completePartialMarkdown(text) {
  if (!text) return text;

  const { masked, fenceAt } = maskCode(text);

  // Inside an unterminated fence everything is literal, so only the text before
  // it can carry an open construct.
  const end = fenceAt === -1 ? text.length : fenceAt;
  const start = lastBlockStart(masked, end);

  let block = text.slice(start, end);

  const link = findOpenLink(masked.slice(start, end), block, block.length);
  if (link) {
    block = block.slice(0, link.start) + link.label;
  }

  block = closeInline(block, maskCode(block).masked, block.length);

  const completed = text.slice(0, start) + block + text.slice(end);

  if (fenceAt === -1) return completed;
  return completed + (completed.endsWith('\n') ? '```' : '\n```');
}
